import { useMemo, useState, useEffect } from 'react';
import loadable from '@loadable/component';
import track, { useTracking } from 'react-tracking';
import { Button } from '@simplywallst/ui-core';
import ReportSection from '@/components/ReportSection';
import ReportSubSection from '@/components/ReportSubSection';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import {
  COMPANY_INTRINSIC_VALUE_CALC,
  COMPANY_PE,
  COMPANY_PEG,
  COMPANY_PB,
} from '@/constants/feedbackSections';
import { ChartType } from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@/utilities/urlGenerator';
/** Raw Data Containers */
import DCFRawData from '../../../DCFRawData';
import {
  LegacyAnchor,
  VALUATION_PRICE_VS_FAIR_VALUE,
  VALUATION_PE_RATIO,
  VALUATION_PE_GROWTH,
  VALUATION_PB_RATIO,
} from '@/constants/anchors';
import { CompanyStatements } from '@/constants/statementIds';
import PageAnchor from '@/components/PageAnchor';
import DeferRenderUntilIntersecting from '@/components/DeferRenderUntilIntersecting';
import Introduction, {
  Metric,
} from '@/pages/CompanyReport/components/Overview/components/IntroductionDeprecated';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import { useVisibleSectionsIsPristine } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { getStatementChecks } from '../../../Introduction/utils';
import type { StatementName } from '@/hooks/useCompanyStatementsAPIFormatted/types';

const ValuationPBRatioRawDataContainer = loadable(
  () => import('@/pages/CompanyReport/components/Value'),
  {
    ssr: false,
    resolveComponent: (component) => component.ValuationPBRatioRawDataContainer,
  }
);
const ValuationPEGRatioRawDataContainer = loadable(
  () => import('@/pages/CompanyReport/components/Value'),
  {
    ssr: false,
    resolveComponent: (component) =>
      component.ValuationPEGRatioRawDataContainer,
  }
);

const ValuationPERatioRawDataContainer = loadable(
  () => import('@/pages/CompanyReport/components/Value'),
  {
    ssr: false,
    resolveComponent: (component) => component.ValuationPERatioRawDataContainer,
  }
);

const PBRatioGauge = loadable(() => import('../PBRatioGauge'), { ssr: false });
const PEGRatioGauge = loadable(() => import('../PEGRatioGauge'), {
  ssr: false,
});
const PERatioGauge = loadable(() => import('../PERatioGauge'), { ssr: false });
const ChartActionsContainer = loadable(
  () => import('@/components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);
const DCF = loadable(() => import('@/components/DCF'), { ssr: false });

const NextStep = loadable(
  () => import('@/pages/CompanyReport/components/SimilarCompanies/NextStep')
);

export interface ValueProps {
  pe: number;
  pb: number;
  mpe: number;
  mpb: number;
  ipe: number;
  ipb: number;
  peg: number;
  isFund: boolean;
  keyMetric: string;
  keyMetricTitle: string;
  companyId: string;
  uniqueSymbol: string;
  companyName: string;
  marketName: string;
  industryName: string;
  tickerSymbol: string;
  sectionNumber?: number;
  intrinsicValue: number;
  sharePrice: number;
  intrinsicDiscount: number;
  intrinsicDiscountFormatted: string;
  intrinsicValueFormatted: string;
  sharePriceFormatted: string;
  skipDefer: boolean;
}

const Value = ({
  pe,
  pb,
  mpe,
  mpb,
  ipe,
  ipb,
  peg,
  marketName,
  industryName,
  isFund,
  companyId,
  sharePrice,
  companyName,
  uniqueSymbol,
  sectionNumber,
  intrinsicValue,
  intrinsicDiscount,
  intrinsicValueFormatted,
  intrinsicDiscountFormatted,
  sharePriceFormatted,
  keyMetric,
  keyMetricTitle,
  skipDefer,
}: ValueProps) => {
  const isPristine = useVisibleSectionsIsPristine();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const [showReport, setShowReport] = useState(true);
  const tracking = useTracking();
  const trackingButtonData = {
    action: 'click',
    modifier: 'show_Meaningless_data',
  };
  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });

  const {
    IsUndervaluedBasedOnDCF,
    IsHighlyUndervaluedBasedOnDCF,
    IsUndervaluedOnPERelativeToPeers,
    IsUndervaluedOnPERelativeToMarket,
    IsUndervaluedBasedOnPEG,
    IsUndervaluedBasedOnPB,
    IsDCFMeaningless,
  } = statements || {};

  const statementChecks = useMemo(() => {
    const VALUE_STATEMENTS: StatementName[] = [
      'IsUndervaluedBasedOnDCF',
      'IsHighlyUndervaluedBasedOnDCF',
      'IsUndervaluedOnPERelativeToPeers',
      'IsUndervaluedOnPERelativeToMarket',
      'IsUndervaluedBasedOnPEG',
      'IsUndervaluedBasedOnPB',
    ];
    return getStatementChecks(VALUE_STATEMENTS, statements);
  }, [statements]);

  const isDataMeaningless = IsDCFMeaningless?.value === true;

  useEffect(() => {
    setShowReport(isDataMeaningless === false);
  }, [isDataMeaningless]);

  const { dcfDownloadUrl, peDownloadUrl, pegDownloadUrl, pbDownloadUrl } =
    useMemo(() => {
      return {
        dcfDownloadUrl: getCompanyInfographicDownloadUrl(
          companyId,
          ChartType.DCF
        ),
        peDownloadUrl: getCompanyInfographicDownloadUrl(
          companyId,
          ChartType.PE
        ),
        pegDownloadUrl: getCompanyInfographicDownloadUrl(
          companyId,
          ChartType.PEG
        ),
        pbDownloadUrl: getCompanyInfographicDownloadUrl(
          companyId,
          ChartType.PB
        ),
      };
    }, [companyId]);

  return (
    <ReportSection sectionNumber={sectionNumber}>
      <Introduction
        title={<CompanySectionTitle title="Valuation" />}
        description={`Is ${companyName} undervalued compared to its fair value and its price relative to the market?`}
        checks={statementChecks}
        section="valuation"
      >
        <Metric metric={keyMetric} description={keyMetricTitle} />
      </Introduction>
      {isDataMeaningless && (
        <ReportSubSection
          body={
            <>
              <p>
                {`In this section, we usually try to help investors determine whether ${companyName} is trading at an attractive price based on the cash flow it is expected to produce in the future. But as ${companyName} has not provided consistent financial data, and the stock also has no analyst forecast or coverage, its intrinsic value cannot be reliably calculated by extrapolating past data or using analyst consensus cash flow predictions.`}
              </p>
              <p>
                This is quite a rare situation as 89% of companies covered by
                Simply Wall St do have a valuation analysis.
              </p>
              {showReport === false && (
                <Button
                  styleType="louder"
                  onClick={() => {
                    tracking.trackEvent(trackingButtonData);
                    setShowReport(!showReport);
                  }}
                >
                  Show section
                </Button>
              )}
            </>
          }
        />
      )}

      {showReport && (
        <>
          <PageAnchor
            type="sub-section-title"
            id={VALUATION_PRICE_VS_FAIR_VALUE}
          />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Share Price vs. Fair Value"
            trackingProps={{ subject: 'chart' }}
            bodyRender={() =>
              intrinsicValue > 0 && (
                <DeferRenderUntilIntersecting
                  preRenderHeight="415"
                  preRenderHeightMdDown="410"
                  skipDefer={skipDefer}
                >
                  {() => (
                    <>
                      <DCF
                        sharePrice={sharePrice}
                        sharePriceFormatted={sharePriceFormatted}
                        intrinsicValue={intrinsicValue}
                        intrinsicValueFormatted={intrinsicValueFormatted}
                        intrinsicDiscount={intrinsicDiscount}
                        intrinsicDiscountFormatted={intrinsicDiscountFormatted}
                        showNewStyle={false}
                      />
                      <ChartActionsContainer
                        companyName={uniqueSymbol}
                        feedbackLabel={COMPANY_INTRINSIC_VALUE_CALC}
                        exportDarkUrl={dcfDownloadUrl}
                      >
                        <DCFRawData companyId={companyId} />
                      </ChartActionsContainer>
                    </>
                  )}
                </DeferRenderUntilIntersecting>
              )
            }
            statements={
              <>
                <Statement
                  title={IsUndervaluedBasedOnDCF?.title}
                  description={
                    <Description statement={IsUndervaluedBasedOnDCF} />
                  }
                  value={IsUndervaluedBasedOnDCF?.value}
                  statementId={CompanyStatements?.valueStatement1}
                />
                <Statement
                  title={IsHighlyUndervaluedBasedOnDCF?.title}
                  description={
                    <Description statement={IsHighlyUndervaluedBasedOnDCF} />
                  }
                  value={IsHighlyUndervaluedBasedOnDCF?.value}
                  statementId={CompanyStatements.valueStatement2}
                />
              </>
            }
          />
          <hr />
          <PageAnchor type="sub-section-title" id={LegacyAnchor.VALUE_PE_PEG} />
          <PageAnchor type="sub-section-title" id={VALUATION_PE_RATIO} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Price To Earnings Ratio"
            trackingProps={{ subject: 'chart' }}
            split={true}
            bodyRender={(title) => (
              <DeferRenderUntilIntersecting
                preRenderHeight="354"
                preRenderHeightMdDown="344"
                skipDefer={skipDefer}
              >
                {() => (
                  <>
                    <PERatioGauge
                      companyPE={pe}
                      companyName={companyName}
                      industryPE={ipe}
                      industryName={industryName}
                      marketPE={mpe}
                      marketName={marketName}
                    />
                    <ChartActionsContainer
                      companyName={uniqueSymbol}
                      rawData={
                        <ValuationPERatioRawDataContainer
                          companyId={companyId}
                        />
                      }
                      rawDataTitle={title}
                      feedbackLabel={COMPANY_PE}
                      exportDarkUrl={peDownloadUrl}
                    />
                  </>
                )}
              </DeferRenderUntilIntersecting>
            )}
            statements={
              <>
                <Statement
                  title={IsUndervaluedOnPERelativeToPeers?.title}
                  description={
                    <Description statement={IsUndervaluedOnPERelativeToPeers} />
                  }
                  value={IsUndervaluedOnPERelativeToPeers?.value}
                  statementId={CompanyStatements.valueStatement3}
                />
                <Statement
                  title={IsUndervaluedOnPERelativeToMarket?.title}
                  description={
                    <Description
                      statement={IsUndervaluedOnPERelativeToMarket}
                    />
                  }
                  value={IsUndervaluedOnPERelativeToMarket?.value}
                  statementId={CompanyStatements.valueStatement4}
                />
              </>
            }
          />
          <hr />
          <PageAnchor type="sub-section-title" id={VALUATION_PE_GROWTH} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Price to Earnings Growth Ratio"
            trackingProps={{ subject: 'chart' }}
            split={true}
            bodyRender={(title) => (
              <DeferRenderUntilIntersecting
                preRenderHeight="316"
                preRenderHeightMdDown="310"
                skipDefer={skipDefer}
              >
                {() => (
                  <>
                    <PEGRatioGauge companyPEG={peg} companyName={companyName} />
                    <ChartActionsContainer
                      companyName={uniqueSymbol}
                      rawData={
                        <ValuationPEGRatioRawDataContainer
                          companyId={companyId}
                        />
                      }
                      rawDataTitle={title}
                      feedbackLabel={COMPANY_PEG}
                      exportDarkUrl={pegDownloadUrl}
                    />
                  </>
                )}
              </DeferRenderUntilIntersecting>
            )}
            statements={
              <Statement
                title={IsUndervaluedBasedOnPEG?.title}
                description={
                  <Description statement={IsUndervaluedBasedOnPEG} />
                }
                value={IsUndervaluedBasedOnPEG?.value}
                statementId={CompanyStatements.valueStatement5}
              />
            }
          />
          <hr />
          <PageAnchor type="sub-section-title" id={VALUATION_PB_RATIO} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title="Price to Book Ratio"
            trackingProps={{ subject: 'chart' }}
            split={true}
            bodyRender={(title) => (
              <DeferRenderUntilIntersecting
                preRenderHeight="354"
                preRenderHeightMdDown="344"
                skipDefer={skipDefer}
              >
                {() => {
                  return (
                    <>
                      <PBRatioGauge
                        companyPB={pb}
                        companyName={companyName}
                        industryPB={ipb}
                        industryName={industryName}
                        marketPB={mpb}
                        marketName={marketName}
                      />
                      <ChartActionsContainer
                        companyName={uniqueSymbol}
                        rawData={
                          <ValuationPBRatioRawDataContainer
                            companyId={companyId}
                          />
                        }
                        rawDataTitle={title}
                        feedbackLabel={COMPANY_PB}
                        exportDarkUrl={pbDownloadUrl}
                      />
                    </>
                  );
                }}
              </DeferRenderUntilIntersecting>
            )}
            statements={
              <Statement
                title={IsUndervaluedBasedOnPB?.title}
                description={<Description statement={IsUndervaluedBasedOnPB} />}
                value={IsUndervaluedBasedOnPB?.value}
                statementId={CompanyStatements.valueStatement6}
              />
            }
          />
        </>
      )}

      {isFund === false && (
        <>
          <hr />
          <NextStep
            section="valuation"
            title="Discover undervalued companies"
          />
        </>
      )}
    </ReportSection>
  );
};

export default track({ type: 'track', subject: 'Value' })(
  Value
) as typeof Value;
